// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Header mit Datum-
// Navigation, Date-Picker und Aktions-Buttons "Pause" / "Alle Abrechnen").
import React, { useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toLocalYMD } from './calendarUtils';

/**
 * Iter 336: Live-Sync-Indikator (kleiner farbiger Punkt + Tooltip).
 * Grün ≤ 45s, Gelb ≤ 180s, Rot > 180s seit letztem erfolgreichen Fetch.
 * Tickt alle 5s damit der Tooltip aktuell bleibt.
 * Klick = Force-Refresh (Iter 336.b).
 */
const LiveSyncIndicator = ({ lastFetchedAt, onRefresh }) => {
  const [now, setNow] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  const handleClick = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  if (!lastFetchedAt) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
        title="Noch nicht synchronisiert — klicken zum Aktualisieren"
        data-testid="calendar-sync-indicator"
      >
        <span className="w-2 h-2 rounded-full bg-gray-300" />
        ‒
      </button>
    );
  }

  const ageSec = Math.max(0, Math.floor((now - new Date(lastFetchedAt).getTime()) / 1000));
  let color = 'bg-emerald-500';
  let label = 'Live';
  if (ageSec > 180) {
    color = 'bg-red-500';
    label = 'Veraltet';
  } else if (ageSec > 45) {
    color = 'bg-amber-500';
    label = 'Aktualisiert';
  }
  const ageLabel = ageSec < 60 ? `vor ${ageSec}s` : `vor ${Math.floor(ageSec / 60)}m`;
  const tooltip = `${label} · zuletzt aktualisiert ${ageLabel} · klicken zum manuellen Refresh`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={refreshing}
      className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-60 cursor-pointer"
      title={tooltip}
      data-testid="calendar-sync-indicator"
    >
      <span
        className={`w-2 h-2 rounded-full ${color} ${
          refreshing ? 'animate-ping' : ageSec <= 45 ? 'animate-pulse' : ''
        }`}
      />
      <span className="hidden sm:inline">
        {refreshing ? 'Aktualisiere…' : `${label} · ${ageLabel}`}
      </span>
    </button>
  );
};

export const CalendarHeader = ({
  currentDate,
  setCurrentDate,
  formattedDate,
  onNavigateDate,
  onOpenBreakDialog,
  onOpenRescheduleDay,
  showAllTrainers,
  setShowAllTrainers,
  showCancelled,
  setShowCancelled,
  onManualBilling,
  billingInProgress,
  lastFetchedAt,
  onRefresh,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-y-2">
    <div>
      <h1 className="text-3xl font-bold flex items-center gap-2">
        <CalendarIcon size={32} />
        Termine
      </h1>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <p className="text-gray-600">{formattedDate}</p>
        {toLocalYMD(currentDate) === toLocalYMD(new Date()) && (
          <span
            className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300"
            data-testid="calendar-today-badge"
          >
            Heute
          </span>
        )}
        {/* Date Picker für schnelle Navigation */}
        <Input
          type="date"
          value={toLocalYMD(currentDate)}
          onChange={(e) => {
            const [y, m, d] = (e.target.value || '').split('-').map(Number);
            if (y && m && d) {
              setCurrentDate(new Date(y, m - 1, d, 12, 0, 0));
            }
          }}
          className="w-auto"
          title="Datum auswählen"
          data-testid="calendar-date-picker"
        />
        <LiveSyncIndicator lastFetchedAt={lastFetchedAt} onRefresh={onRefresh} />
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onNavigateDate(-1)}
          data-testid="calendar-prev-btn"
        >
          <ChevronLeft size={20} />
        </Button>
        <Button
          variant="outline"
          onClick={() => setCurrentDate(new Date())}
          data-testid="calendar-today-btn"
        >
          Heute
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => onNavigateDate(1)}
          data-testid="calendar-next-btn"
        >
          <ChevronRight size={20} />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => setShowAllTrainers(!showAllTrainers)}
          variant="outline"
          className={`flex items-center gap-2 ${showAllTrainers ? 'bg-teal-50 border-teal-400 text-teal-700' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}
          title={showAllTrainers ? 'Nur Trainer mit Arbeitszeit an diesem Tag anzeigen' : 'Alle Trainer anzeigen, auch ohne Arbeitszeit an diesem Tag'}
          data-testid="show-all-trainers-btn"
        >
          {showAllTrainers ? '👥 Alle Trainer ✓' : '👥 Alle Trainer'}
        </Button>
        <Button
          onClick={() => setShowCancelled(!showCancelled)}
          variant="outline"
          className={`flex items-center gap-2 ${showCancelled ? 'bg-zinc-200 border-zinc-400 text-zinc-800' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}
          title={showCancelled ? 'Abgesagte Termine wieder ausblenden' : 'Abgesagte Termine (grau) im Kalender einblenden'}
          data-testid="show-cancelled-btn"
        >
          {showCancelled ? '🚫 Abgesagte ✓' : '🚫 Abgesagte'}
        </Button>
        <Button
          onClick={onOpenRescheduleDay}
          variant="outline"
          className="flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
          data-testid="reschedule-day-btn"
        >
          ⇄ Tag verschieben
        </Button>
        <Button
          onClick={onOpenBreakDialog}
          variant="outline"
          className="flex items-center gap-2 border-zinc-300 text-zinc-700 hover:bg-zinc-100"
          data-testid="add-break-btn"
        >
          ☕ Pause
        </Button>
        <Button
          onClick={() => window.dispatchEvent(new CustomEvent('open-series-history'))}
          variant="outline"
          className="flex items-center gap-2 border-zinc-300 text-zinc-700 hover:bg-zinc-100"
          title="Zuletzt im Hintergrund gebuchte/gelöschte Serien — mit Rückgängig"
          data-testid="open-series-history-btn"
        >
          🕘 Verlauf
        </Button>
        <Button
          onClick={onManualBilling}
          variant="default"
          disabled={billingInProgress}
          className={`flex items-center gap-2 ${
            billingInProgress
              ? 'bg-orange-400 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-700'
          }`}
          data-testid="manual-billing-btn"
        >
          {billingInProgress ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Wird verarbeitet...
            </>
          ) : (
            <>
              <CheckCircle size={18} />
              Alle Abrechnen
            </>
          )}
        </Button>
      </div>
    </div>
  </div>
);

export default CalendarHeader;
